import React, { useEffect, useState } from 'react';
import Slider, { Mark } from '@material-ui/core/Slider';
import classNames from 'classnames';
import { FormFieldBase, getFieldProperties, getFieldPropertyValues, getOptionsHashMap, getOptionValue } from './';
import { FormFieldInputConfig, FormFieldPropertyType } from './form-field-input';
import ValidationMessage from './validation-message';
import styled from 'styled-components';

type HashMapItemType = Record<string, Record<string, unknown>>;

interface ISliderContainerProps {
  $optionCount: number;
}

const styles = {
  SliderContainer: styled.div.attrs<ISliderContainerProps>(props => ({
    $optionCount: props.$optionCount || 2,
  }))`
    margin: 0 15px;

    span.MuiSlider-markLabel {
      max-width: ${(props: ISliderContainerProps) => {
        const maxWidth = Math.round(100 / props.$optionCount);

        return `${maxWidth}%`;
      }};
    }

    span.MuiSlider-markLabel:has(label.first),
    span.MuiSlider-markLabel:has(label.last) {
      max-width: ${(props: ISliderContainerProps) => {
        const maxWidth = Math.round(100 / props.$optionCount) / 2;

        return `${maxWidth}%`;
      }};
    }
  `
};

export type FormFieldSlidingScaleProps = {
  field: FormFieldInputConfig;
  className: string;
  commonProps: { [K in FormFieldPropertyType]?: any };
  isRenderedField: boolean;
  onChange: Function;
  value: string;
  validationMessage: unknown[] | string;
  readOnly: boolean;
};

interface ImageOption {
  src: string;
  alt: string;
  height: string;
  width: string;
}

/**
 * extends the Slider Mark type
 * Slider values are constrained as type Number, but we use the user input option string value, 
 * or the image alt with fallback to the src.
 * The below helper methods are used to handle this conversion.
 */
interface IMark extends Mark {
  index: number;
  formValue: string;
}

// converts the stored data options to material-ui Mark types for the Slider 
const convertOptionsToMarks = (options: (string | ImageOption)[]): Mark[] => {
  if (options.length < 2) {
    return [];
  }

  // calculate the percent-based positioning of the marks
  const scale = Math.round(100 / (options.length - 1));

  return options.map((option: string | ImageOption, idx) => {
    // first and last labels are positioned to edge of Slider, not overflowing
    let labelClass;
    let maxWidth = scale;
    if (idx === 0) {
      labelClass = 'first';
      maxWidth = scale / 2;
    }
    if (idx === options.length - 1) {
      labelClass = 'last';
      maxWidth = scale / 2;
    }
    if (option.hasOwnProperty('src')) {
      const currentOption = option as ImageOption;

      return {
        value: idx * scale,
        label: (
          <img
            src={currentOption.src}
            alt={currentOption.alt}
            height={currentOption.height}
            width={currentOption.width}
            className={labelClass}
            title={currentOption.alt}
          />
        ),
        formValue: currentOption.alt || currentOption.src,
        index: idx,
      };
    }

    return {
      value: idx * scale,
      label: <label className={labelClass} title={`${option}`}>{option}</label>,
      formValue: option,
      index: idx,
    };
  });
};

// given a form value, find the corresponding Slider value
const getValueFromFormValue = (value: string, marks: Mark[]): number => {
  // we should only have this hyphenated value when there are
  // duplicate options with the same label
  const valueSplit = value.split('--');
  const isDuplicated = valueSplit.length > 1;
  const valueIndex = Number(isDuplicated ? valueSplit[1] : 0);

  // so if the selected value is a duplicated one, 
  // find it by the hyphenated index value stored.
  // otherwise find it by value.
  const mark = isDuplicated
    ? marks[valueIndex]
    : marks.find(mark => (mark as IMark).formValue?.trim() === value?.trim());

  if (mark) {
    return mark.value;
  }
  throw new Error('getValueFromFormValue: formValue not found');
};

// given a Slider value, find the corresponding form value
const getFormValueByValue = (value: number, marks: Mark[]): IMark => {
  const mark = marks.find(mark => mark.value === value);
  if (mark) {
    return mark as IMark;
  }
  throw new Error('getFormValueByValue: value not found');
};

export const FormFieldSlidingScale: React.FC<FormFieldSlidingScaleProps> = props => {
  const { field, className, commonProps, onChange, value, validationMessage, readOnly } = props;
  const properties: { [key: string]: any } = getFieldProperties(field, 'specific');
  const propertyValues: { [key: string]: any } = getFieldPropertyValues(
    properties.specific,
    'options',
  );
  const options = propertyValues?.options;

  const [marks, setMarks] = useState<Mark[]>([]);
  const [selectedValue, setSelectedValue] = useState<number>(0);
  const [optionsHash, setOptionsHash] = useState<HashMapItemType>({});

  useEffect(() => {
    if (options && options.length) {
      setMarks(convertOptionsToMarks(options));
      setOptionsHash(getOptionsHashMap(options) as HashMapItemType);
    }
  }, [options]);

  useEffect(() => {
    if (value && marks.length) {
      setSelectedValue(getValueFromFormValue(value, marks));
    }
  }, [value, marks]);

  const classes = classNames({
    [className]: true,
    'form-field-sliding-scale': true,
    'control-group error': validationMessage,
  });

  // handleChange is being called via the slider component's `onChange` prop
  // instead of `onChangeCommitted`. The latter only executes the handler method
  // once after the `mouseup` event occurs, but it prevents the UI from updating to
  // each mark as it is moused over. `onChange` allows this, but fires way more than
  // is needed so the compromise is to bail out of the handler if the value hasn't actually changed.
  const handleChange = (_event: React.ChangeEvent<{}>, value: number | number[]) => {
    if (value === selectedValue) {
      return;
    }

    if (onChange) {
      const { formValue, index } = getFormValueByValue(Number(value), marks);
      const hashMapItem = optionsHash[formValue];
      const isDuplicated = hashMapItem && Object.keys(hashMapItem).length > 1;
      const optionValue = hashMapItem && getOptionValue(hashMapItem[index], 'alt');
      const includeIndexInValue = isDuplicated || !optionValue;
      const saveValue = includeIndexInValue ? `${optionValue}--${index}` : optionValue;

      // spoofing the event for the upstream handler
      onChange({
        target: {
          id: field.uuid,
          value: saveValue,
        },
      });
    }
  };

  return (
    <div className={classes}>
      <div className='form-field-label primary'>{`${commonProps.label} ${
        commonProps.makeRequired ? '*' : ''
      }`}</div>
      <styles.SliderContainer $optionCount={options.length}>
        <Slider
          defaultValue={0}
          value={selectedValue}
          marks={marks}
          step={null}
          onChange={handleChange}
          aria-labelledby='slider'
          disabled={readOnly}
        />
      </styles.SliderContainer>
      {commonProps.sublabel && (
        <label className='form-field-label secondary'>{commonProps.sublabel}</label>
      )}
      <ValidationMessage message={validationMessage} />
    </div>
  );
};

export default FormFieldBase(FormFieldSlidingScale);